"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useRef, useState, useEffect } from "react";
import { ArrowRight, Calendar, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const timelineEvents = [{
  id: 1,
  title: "Sabor Series: West Colfax",
  date: "May 12, 2024",
  location: "40 West Arts District",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_series_west_colfax.png",
  description: "Kickoff the season with a vibrant street art and culinary fusion.",
  link: "/events/west-colfax"
}, {
  id: 2,
  title: "Sabor Series: Aurora",
  date: "June 08, 2024",
  location: "Stanley Marketplace",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_series_aurora.png",
  description: "A global tasting experience celebrating the diversity of Aurora.",
  link: "/events/aurora"
}, {
  id: 3,
  title: "Sabor Series: Cherry Creek",
  date: "July 15, 2024",
  location: "Fillmore Plaza",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_series_cherry_creek.png",
  description: "Luxury meets Latin flair in the heart of Denver's premier shopping district.",
  link: "/events/cherry-creek"
}, {
  id: 4,
  title: "Sabor Series: Five Points",
  date: "August 03, 2024",
  location: "Welton Street",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_series_five_points.png",
  description: "Celebrating jazz, history, and soul food with a spicy twist.",
  link: "/events/five-points"
}, {
  id: 5,
  title: "SABOR DENVER: Main Event",
  date: "August 24, 2024",
  location: "Denver Botanic Gardens",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_main_event.png",
  description: "The premier multicultural culinary and corporate gala of the year.",
  link: "/events/main-event"
}, {
  id: 6,
  title: "Sabor After Dark",
  date: "August 24, 2024",
  location: "Clayton Members Club",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557866/img/sabor_after_party.png",
  description: "The exclusive official after-party for VIPs and sponsors.",
  link: "/events/after-party"
}];
export default function TheSaborContinuum() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="the-sabor-continuum" ref={ref} className={cn("dark bg-background text-foreground relative overflow-hidden py-16 md:py-24", isVisible ? "opacity-100" : "opacity-0 transition-opacity duration-1000")}>
      <div className="container mx-auto px-4 md:px-8 h-full">
        <div className="flex flex-col lg:flex-row h-full gap-8 lg:gap-0">
          
          {}
          <div className="lg:w-32 xl:w-48 flex-shrink-0 flex lg:flex-col justify-center lg:justify-start items-center lg:items-start lg:border-r border-border/30 lg:pr-8 relative z-10">
            <div className="lg:sticky lg:top-24 lg:h-[60vh] flex items-center justify-center">
              <h2 className="text-4xl md:text-5xl lg:text-7xl font-serif font-bold text-primary tracking-wider lg:[writing-mode:vertical-rl] lg:rotate-180 whitespace-nowrap uppercase drop-shadow-lg">
                The Sabor Continuum
              </h2>
            </div>
          </div>

          {}
          <div className="flex-grow relative min-h-[600px] lg:min-h-[700px] w-full overflow-hidden">
            <ScrollArea className="w-full h-full rounded-none" orientation="horizontal">
              <div className="flex items-center h-[600px] lg:h-[700px] px-8 lg:px-16 relative min-w-max">
                
                {}
                <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-80 z-0" />
                
                {}
                <div className="flex gap-12 md:gap-24 lg:gap-32 relative z-10 py-12">
                  {timelineEvents.map((event, index) => {
                  const isEven = index % 2 === 0;
                  return <div key={event.id} data-index={index} className={cn("w-[300px] md:w-[350px] flex flex-col relative group", isEven ? "justify-end mb-12" : "justify-start mt-12")} style={{
                    transform: isEven ? "translateY(-25%)" : "translateY(25%)"
                  }}>
                        {}
                        <div className={cn("absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-primary bg-background z-20 transition-all duration-300 group-hover:bg-primary group-hover:scale-125 shadow-[0_0_15px_rgba(var(--primary),0.5)]", isEven ? "-bottom-[calc(25%-2px)]" : "-top-[calc(25%-2px)]")} style={{
                      top: isEven ? "auto" : "-3rem",
                      bottom: isEven ? "-3rem" : "auto"
                    }} />
                        
                        {}
                      </div>;
                })}

                  {}
                  {}
                </div>

                <div className="flex gap-8 md:gap-16 lg:gap-24 relative z-10 h-full items-center">
                   {timelineEvents.map((event, index) => {
                  const isAbove = index % 2 === 0;
                  return <div key={event.id} data-index={index} className="flex flex-col items-center h-full justify-center relative w-[320px]">
                         {}
                         <div className={cn("relative flex flex-col gap-4 p-6 rounded-xl border border-primary/20 bg-card/50 backdrop-blur-sm hover:bg-card/80 transition-all duration-500 hover:border-primary/50 hover:shadow-[0_0_30px_-10px_rgba(var(--primary),0.3)] group w-full", isAbove ? "mb-16 md:mb-32" : "mt-16 md:mt-32")}>
                            {}
                            <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-border/50">
                              <img src={event.image} alt={event.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-60" />
                            </div>

                            {}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-xs font-medium text-primary uppercase tracking-widest">
                                <span className="flex items-center gap-1">
                                  <SafeHydrate><Calendar className="w-3 h-3" /></SafeHydrate> {event.date}
                                </span>
                              </div>
                              
                              <h3 className="text-xl font-serif font-bold text-foreground group-hover:text-primary transition-colors">
                                {event.title}
                              </h3>
                              
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {event.description}
                              </p>

                              <div className="flex items-center gap-1 text-xs text-muted-foreground/80">
                                <MapPin className="w-3 h-3" /> {event.location}
                              </div>
                            </div>

                            {}
                            <div className="pt-2">
                              <Button asChild variant="outline" size="sm" className="w-full border-primary/30 hover:bg-primary hover:text-primary-foreground group-hover:border-primary">
                                <Link to={event.link}>
                                  Learn More <ArrowRight className="w-3 h-3 ml-1" />
                                </Link>
                              </Button>
                            </div>

                            {}
                            <div className={cn("absolute left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-primary/0 via-primary/50 to-primary/0 h-16 md:h-32", isAbove ? "-bottom-16 md:-bottom-32" : "-top-16 md:-top-32")} />
                            
                            {}
                            <div className={cn("absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary shadow-[0_0_10px_rgba(var(--primary),0.8)] z-20", isAbove ? "-bottom-[calc(4rem+6px)] md:-bottom-[calc(8rem+6px)]" : "-top-[calc(4rem+6px)] md:-top-[calc(8rem+6px)]")} />
                         </div>
                       </div>;
                })}
                   
                   {}
                   <div className="w-24 shrink-0" />
                </div>

              </div>
              <ScrollBar orientation="horizontal" className="bg-muted/20" />
            </ScrollArea>
          </div>
        </div>
      </div>
    </section>;
}